import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, RefreshCw, Package, Plus, Eye, Clock, CheckCircle, XCircle, CheckCheck
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import * as categoryApi from '@/services/categoryApi';

const MyShipmentRequests = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  
  // View dialog
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showViewDialog, setShowViewDialog] = useState(false);

  const fetchRequests = useCallback(async () => {
    try {
      setLoading(true);
      const data = await categoryApi.getShipmentRequests({
        status: statusFilter === 'all' ? undefined : statusFilter
      });
      setRequests(data.requests || []);
    } catch (error) {
      toast.error('Failed to fetch requests');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const handleViewRequest = async (requestId) => {
    try {
      const data = await categoryApi.getShipmentRequest(requestId);
      setSelectedRequest(data);
      setShowViewDialog(true);
    } catch (error) {
      toast.error('Failed to fetch request details');
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      pending: { icon: Clock, className: 'bg-yellow-100 text-yellow-700 border-yellow-300', label: 'PENDING' },
      approved: { icon: CheckCircle, className: 'bg-green-100 text-green-700 border-green-300', label: 'APPROVED' },
      in_progress: { icon: Package, className: 'bg-blue-100 text-blue-700 border-blue-300', label: 'IN PROGRESS' },
      completed: { icon: CheckCheck, className: 'bg-gray-100 text-gray-700 border-gray-300', label: 'COMPLETED' },
      rejected: { icon: XCircle, className: 'bg-red-100 text-red-700 border-red-300', label: 'REJECTED' }
    };
    const c = config[status] || config.pending;
    const Icon = c.icon;
    return (
      <Badge className={`${c.className} border flex items-center gap-1`}>
        <Icon className="w-3 h-3" />
        {c.label}
      </Badge>
    );
  };

  const getPriorityBadge = (priority) => {
    const styles = {
      urgent: 'bg-red-500 text-white',
      high: 'bg-orange-500 text-white',
      normal: 'bg-blue-500 text-white',
      low: 'bg-gray-400 text-white'
    };
    return priority ? <Badge className={styles[priority] || styles.normal}>{priority.toUpperCase()}</Badge> : null;
  };

  const getModuleName = (code) => {
    const names = {
      greige: 'Greige Section',
      greige_storage: 'Greige Storage',
      fob: 'FOB Section',
      goods_transfer: 'Goods Transfer'
    };
    return names[code] || code;
  };

  const stats = {
    pending: requests.filter(r => r.status === 'pending').length,
    approved: requests.filter(r => ['approved', 'in_progress'].includes(r.status)).length,
    completed: requests.filter(r => r.status === 'completed').length,
    rejected: requests.filter(r => r.status === 'rejected').length
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/category/incharge')} className="text-[#F9FAFB] hover:bg-white/10" data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold font-display">My Shipment Requests</h1>
              <p className="text-sm text-[#E8DCC8]">Track your submitted requests</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              onClick={() => navigate('/category/incharge')} 
              className="bg-[#2D5F4F] hover:bg-[#1A1F2E]"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Request
            </Button>
            <Button variant="outline" size="sm" onClick={fetchRequests} className="border-white/20 text-white hover:bg-white/10" data-testid="refresh-btn">
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card className="border-yellow-200 bg-yellow-50">
            <CardContent className="py-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-yellow-600">Pending</p>
                  <p className="text-2xl font-bold text-yellow-700">{stats.pending}</p>
                </div>
                <Clock className="w-6 h-6 text-yellow-500" />
              </div>
            </CardContent>
          </Card>
          <Card className="border-green-200 bg-green-50">
            <CardContent className="py-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-green-600">Approved/In Progress</p>
                  <p className="text-2xl font-bold text-green-700">{stats.approved}</p>
                </div>
                <CheckCircle className="w-6 h-6 text-green-500" />
              </div>
            </CardContent>
          </Card>
          <Card className="border-gray-200 bg-gray-50">
            <CardContent className="py-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-600">Completed</p>
                  <p className="text-2xl font-bold text-gray-700">{stats.completed}</p>
                </div>
                <CheckCheck className="w-6 h-6 text-gray-500" />
              </div>
            </CardContent>
          </Card>
          <Card className="border-red-200 bg-red-50">
            <CardContent className="py-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-red-600">Rejected</p>
                  <p className="text-2xl font-bold text-red-700">{stats.rejected}</p>
                </div>
                <XCircle className="w-6 h-6 text-red-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filter */}
        <Card className="border-[#E5DFD0] mb-6">
          <CardContent className="py-4">
            <div className="flex items-center gap-4">
              <Label className="text-sm font-medium">Filter by Status:</Label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Requests Table */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-8 h-8 animate-spin text-[#2D5F4F]" />
          </div>
        ) : requests.length === 0 ? (
          <Card className="border-[#E5DFD0]">
            <CardContent className="py-12 text-center">
              <Package className="w-12 h-12 mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-semibold text-[#1A1F2E] mb-2">No Requests Found</h3>
              <p className="text-[#5A5F6E] mb-4">You haven't submitted any shipment requests yet</p>
              <Button onClick={() => navigate('/category/incharge')} className="bg-[#2D5F4F]">
                <Plus className="w-4 h-4 mr-2" />
                Create New Request
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Card className="border-[#E5DFD0]">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Package className="w-5 h-5" />
                My Requests
                <Badge variant="outline" className="ml-2">{requests.length}</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead>Request ID</TableHead>
                      <TableHead>Module</TableHead>
                      <TableHead>Items</TableHead>
                      <TableHead>Total Qty</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Priority</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead className="text-center">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {requests.map((request) => (
                      <TableRow key={request.request_id} className="hover:bg-gray-50">
                        <TableCell className="font-mono text-sm font-medium">{request.request_id}</TableCell>
                        <TableCell>{getModuleName(request.module)}</TableCell>
                        <TableCell>{request.items?.length || 0}</TableCell>
                        <TableCell>
                          <Badge className="bg-blue-100 text-blue-700 border-0">{request.total_shipment_qty}</Badge>
                        </TableCell>
                        <TableCell>{getStatusBadge(request.status)}</TableCell>
                        <TableCell>{getPriorityBadge(request.priority)}</TableCell>
                        <TableCell className="text-sm text-gray-500">
                          {new Date(request.created_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleViewRequest(request.request_id)}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* View Dialog */}
        <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
          <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Request Details - {selectedRequest?.request_id}</DialogTitle>
            </DialogHeader>
            
            {selectedRequest && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 bg-gray-50 p-4 rounded-lg">
                  <div>
                    <p className="text-xs text-gray-500">Module</p>
                    <p className="font-semibold">{getModuleName(selectedRequest.module)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Status</p>
                    {getStatusBadge(selectedRequest.status)}
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Priority</p>
                    {getPriorityBadge(selectedRequest.priority) || <span className="text-gray-400">Not set</span>}
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Total Qty</p>
                    <p className="font-semibold">{selectedRequest.total_shipment_qty}</p>
                  </div>
                </div>
                
                {/* Status Timeline */}
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-sm mb-3">Timeline</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      <span>Created: {new Date(selectedRequest.created_at).toLocaleString()}</span>
                    </div>
                    {selectedRequest.approved_at && (
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        <span>Approved by {selectedRequest.approved_by_name}: {new Date(selectedRequest.approved_at).toLocaleString()}</span>
                      </div>
                    )}
                    {selectedRequest.completed_at && (
                      <div className="flex items-center gap-2">
                        <CheckCheck className="w-4 h-4 text-blue-500" />
                        <span>Completed by {selectedRequest.completed_by_name}: {new Date(selectedRequest.completed_at).toLocaleString()}</span>
                      </div>
                    )}
                    {selectedRequest.rejected_at && (
                      <div className="flex items-center gap-2">
                        <XCircle className="w-4 h-4 text-red-500" />
                        <span>Rejected by {selectedRequest.rejected_by_name}: {new Date(selectedRequest.rejected_at).toLocaleString()}</span>
                      </div>
                    )}
                  </div>
                </div>
                
                {selectedRequest.rejection_remarks && (
                  <div className="bg-red-50 p-3 rounded-lg border border-red-200">
                    <p className="text-xs text-red-600 mb-1">Rejection Reason</p>
                    <p className="text-sm">{selectedRequest.rejection_remarks}</p>
                  </div>
                )}
                
                <div>
                  <h4 className="font-semibold mb-2">Items ({selectedRequest.items?.length || 0})</h4>
                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-gray-50">
                          <TableHead>Barcode</TableHead>
                          <TableHead>Design</TableHead>
                          <TableHead>Color</TableHead>
                          <TableHead>Closing Qty</TableHead>
                          <TableHead>Ship Qty</TableHead>
                          <TableHead>Target FG</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {selectedRequest.items?.map((item, idx) => (
                          <TableRow key={idx}>
                            <TableCell className="font-mono text-sm">{item.barcode}</TableCell>
                            <TableCell>{item.design_name}</TableCell>
                            <TableCell>{item.color || '-'}</TableCell>
                            <TableCell>{item.closing_qty}</TableCell>
                            <TableCell className="font-semibold">{item.shipment_qty}</TableCell>
                            <TableCell>{item.target_fg_name || item.design_name}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              </div>
            )}
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowViewDialog(false)}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default MyShipmentRequests;
